<template>
<!-- eslint-disable max-len -->
<div class="container">
<div class="row no-gutters">
<div class="login-form col-sm-12">

  <h2>Usage Data Report Generator</h2>
  <div style="font-size: 75%; margin-bottom: 10px;">
    If not all playthroughs are marked complete, note that recent progress may not show up in this data until later, as the data takes time to process.
  </div>

  <select-input
    label
    class="col-sm-6"
    v-model="selectedConfigName"
    @input="onConfigSelected"
    :options="configs"
    use="name"
    :display="g => { return `${g.name} (${(new Date(g.updated)).toLocaleString()})`; }"
    :remove="selectedConfigName ? deleteConfig : null"
  >
    Saved Report Configuration
  </select-input>
  <b-input-group style="left: 15px;">
    <text-input
      label
      v-model="configNameToSave"
    >
      Report Configuration Save Name
    </text-input>
    <b-input-group-append>
      <button
        class="btn"
        style="height: 40%; margin-top: 33px;"
        :disabled="
          !((classesToInclude.length && classesToInclude[0] && classesToInclude[0]._id)
          || (gamesToInclude.length && gamesToInclude[0] && gamesToInclude[0]._id)
          || (requireClass && canManageAllReportConfigurations))
        "
        @click.prevent="saveConfig"
      >
        Save Report Configuration
      </button>
    </b-input-group-append>
  </b-input-group>
  <button
    class="btn"
    style="margin-left: 15px; margin-top: 0px; margin-bottom: 20px;"
    @click.prevent="openHcadUrl"
    v-if="activeEnvironment.hcadUrl && activeEnvironment.hcadUrl.length > 0"
  >
    Open Reporting Dashboard
  </button>
  <div style="margin-bottom: 15px; height: 3px; width: 100%; background-color: black;"/>

  <form>
    <array-input
      v-if="games.length > 0 && (canManageAllReportConfigurations || !requireClass)"
      :items="gamesToInclude"
      :hide-button="gamesToInclude.length >= games.length"
    >
      <h5 slot="title">Included Gamulations</h5>

      <div class="form-row" slot="item" slot-scope="{ item: includedGame, index: i }">
        <select-input
          class="col-sm-6"
          v-model="includedGame._id"
          :options="unused(games).by('_id').in(gamesToInclude).by('_id').at(i)"
          use="_id"
          :display="g => { return `${g.name}`; }"
          required
          autofocus
          :to="(id) => { return `/blade/games/${id}/edit`; }"
          :remove="() => { gamesToInclude.splice(i, 1); updateMetrics(); }"
          @change="updateMetrics"
        >
          Gamulation
        </select-input>
      </div>
      <span slot="button">Include Gamulation</span>
    </array-input>

    <array-input
      :items="classesToInclude"
      :hide-button="classesToInclude.length >= classes.length"
    >
      <h5 slot="title">Included {{classTypeNamePlural}}</h5>

      <div class="form-row" slot="item" slot-scope="{ item: includedClass, index: i }">
        <select-input
          class="col-sm-6"
          v-model="includedClass._id"
          :options="unused(classes).by('_id').in(classesToInclude).by('_id').at(i)"
          use="_id"
          :display="c => { return `${c.className}`; }"
          required
          autofocus
          :to="(id) => { return `/blade/classes/${id}/edit`; }"
          :remove="() => { classesToInclude.splice(i, 1); updateMetrics(); }"
          @change="updateMetrics"
        >
          {{classTypeName}}
        </select-input>
      </div>
      <span slot="button">Include {{classTypeName}}</span>
    </array-input>

    <template
      v-if="
        (classesToInclude.length && classesToInclude[0] && classesToInclude[0]._id)
        || (gamesToInclude.length && gamesToInclude[0] && gamesToInclude[0]._id)
        || canManageAllReportConfigurations
      "
    >
      <checkbox-input v-model="requireClass">Require {{classTypeName}}</checkbox-input>
      <checkbox-input v-model="includeEmail">Include Email</checkbox-input>
      <checkbox-input v-model="includeName">Include Name</checkbox-input>
      <checkbox-input v-model="includeStudentId">Include Student/Employee ID</checkbox-input>
      <checkbox-input v-model="includeLastSignedInTimestamp">Include Last Signed In Timestamp</checkbox-input>
      <checkbox-input v-model="includeLastActiveTimestamp">Include Last Active Timestamp</checkbox-input>
      <checkbox-input v-model="includeHelpClicks">Include Clicks Of Help Button</checkbox-input>
      <checkbox-input v-model="includeActivity">Include Activity</checkbox-input>
      <checkbox-input v-model="includeIndividualDecisionProgress">Include Minor Decision Progress</checkbox-input>
      <checkbox-input v-model="includeTeamDecisionProgress">Include Major Decision Progress</checkbox-input>
      <checkbox-input v-model="includeLastQuestionAnsweredTimestamp">Include Last Question Answered Timestamp</checkbox-input>
      <checkbox-input v-model="includeCompletedTimestamp">Include Completed Timestamp</checkbox-input>
      <checkbox-input v-model="includeDuration">Include Duration</checkbox-input>
      <checkbox-input v-model="includeCash">Include Cash</checkbox-input>
      <checkbox-input v-model="perChallengeTeamMetrics">Show Team Metrics For Each Section</checkbox-input>
      <checkbox-input v-model="includePercentIndividualMetrics">Include Individual Metric Percentages</checkbox-input>

      <array-input
        :items="metricsToInclude"
        :hide-button="metricsToInclude.length >= metrics.length"
      >
        <h5 slot="title">Included Metrics</h5>

        <div class="form-row" slot="item" slot-scope="{ item: includedMetric, index: i }">
          <select-input
            class="col-sm-6"
            v-model="includedMetric._id"
            :options="unused(metrics).by('_id').in(metricsToInclude).by('_id').at(i)"
            use="_id"
            :display="m => { return `${m.name} (${m.type})`; }"
            required
            autofocus
            :to="(id) => { return `/blade/metrics/${id}/edit`; }"
            :remove="() => { metricsToInclude.splice(i, 1); }"
          >
            Metric
          </select-input>
        </div>
        <span slot="button">Include Metric</span>
      </array-input>

      <array-input
        :items="referencesToInclude"
      >
        <h5 slot="title">Included References</h5>

        <div class="form-row" slot="item" slot-scope="{ item: includedReference, index: i }">
          <text-input
            class="col-sm-6"
            v-model="includedReference.referenceId"
            required
            autofocus
            :remove="() => { referencesToInclude.splice(i, 1); }"
          >
            Reference ID
          </text-input>
        </div>
        <span slot="button">Include Reference</span>
      </array-input>

      <checkbox-input v-model="includeAllUsers">
        Include All Users (don't limit to {{classTypeNameLower}} students/employees or users with the gamulation assigned)
      </checkbox-input>
    </template>

    <button
      type="submit"
      class="btn"
      @click.prevent="onSubmit"
      :disabled="
        submitted
        || !((classesToInclude.length && classesToInclude[0] && classesToInclude[0]._id)
          || (gamesToInclude.length && gamesToInclude[0] && gamesToInclude[0]._id))
      "
    >
      <span style="margin: 0 3px;">Download Usage Data</span>
    </button>
  </form>
  <router-link
    :to="`/blade/reasonData`"
    class="btn"
    style="
      margin-top: 12px;
      padding: 5px;
      position: absolute;
      top: 0;
      right: 12px;
    "
  >
    <span style="margin: 0 3px;">Decision Data</span>
  </router-link>
</div>
</div>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FileSaver from 'file-saver';

import ArrayInput from '../../ui/inputs/ArrayInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import TextInput from '../../ui/inputs/TextInput';
import SelectInput from '../../ui/inputs/SelectInput';

import { unused } from '../../../utils/utils';
import { objectToCSV } from '../../../utils/csv-util';

export default {
  components: {
    ArrayInput,
    CheckboxInput,
    TextInput,
    SelectInput
  },
  data() {
    return {
      includeEmail: false,
      includeName: false,
      includeStudentId: false,
      includeLastSignedInTimestamp: false,
      includeLastActiveTimestamp: false,
      includeActivity: false,
      includeIndividualDecisionProgress: false,
      includeTeamDecisionProgress: false,
      includeLastQuestionAnsweredTimestamp: false,
      includeCompletedTimestamp: false,
      includeDuration: false,
      includeCash: false,
      perChallengeTeamMetrics: false,
      includePercentIndividualMetrics: false,
      includeAllUsers: false,
      metricsToInclude: [],
      referencesToInclude: [],
      gamesToInclude: [],
      classesToInclude: [],
      requireClass: false,
      includeHelpClicks: false,
      submitted: false,
      metrics: [],
      games: [],
      classes: [],
      configs: [],
      selectedConfigName: '',
      configNameToSave: '',
      unused
    };
  },
  computed: {
    ...mapGetters([
      'canSelectGame',
      'classTypeName',
      'classTypeNamePlural',
      'classTypeNameLower',
      'activeEnvironment',
      'canManageAllReportConfigurations',
      'authToken'
    ]),
    hcadUrl() {
      let base = this.activeEnvironment.hcadUrl;
      const hashIdx = base.indexOf('#');
      if (hashIdx > -1) {
        base = `${base.substring(0, hashIdx - 1)
        }?token=${encodeURIComponent(this.authToken)}&tabtext=${encodeURIComponent(this.activeEnvironment.tabText)}${
          base.substring(hashIdx)}`;
      } else {
        base += `?token=${encodeURIComponent(this.authToken)}&tabtext=${encodeURIComponent(this.activeEnvironment.tabText)}`;
      }
      return base;
    },
  },
  async created() {
    await this.updateMetrics();
    try {
      this.games = ((this.canSelectGame) ? (await this.getGames()) : await this.getAssignedGames());
      this.classes = await this.getClasses();
      this.configs = await this.getUsageDataConfigs();
    } catch (err) {
      const message = 'Error getting gamulations/classes/report configurations, please refresh';
      this.flashDanger({ message });
    }
  },
  methods: {
    ...mapActions([
      'getUsageData',
      'getUsageDataConfigs',
      'getUsageDataConfig',
      'createUsageDataConfig',
      'deleteUsageDataConfig',
      'getAssignedMetrics',
      'getClasses',
      'getGames',
      'getAssignedGames',
      'flashPrimary',
      'flashSuccess',
      'flashDanger'
    ]),
    openHcadUrl() {
      window.open(this.hcadUrl, '_blank');
    },
    async updateMetrics() {
      try {
        this.metrics = await this.getAssignedMetrics({
          gamesToInclude: !(this.gamesToInclude) ? [] : this.gamesToInclude.filter(g => g && g._id),
          classesToInclude: !(this.classesToInclude) ? [] : this.classesToInclude.filter(c => c && c._id),
        });
      } catch (err) {
        const message = 'Error getting metrics, please refresh';
        this.flashDanger({ message });
      }
    },
    async onSubmit() {
      if (this.submitted) return;
      this.submitted = true;

      try {
        const defaultDateOptions = Intl.DateTimeFormat().resolvedOptions();

        this.flashPrimary({ message: 'Please wait. Usage data downloading...' });

        const dataObject = await this.getUsageData({
          includeEmail: this.includeEmail,
          includeName: this.includeName,
          includeStudentId: this.includeStudentId,
          includeLastSignedInTimestamp: this.includeLastSignedInTimestamp,
          includeLastActiveTimestamp: this.includeLastActiveTimestamp,
          includeActivity: this.includeActivity,
          includeIndividualDecisionProgress: this.includeIndividualDecisionProgress,
          includeTeamDecisionProgress: this.includeTeamDecisionProgress,
          includeLastQuestionAnsweredTimestamp: this.includeLastQuestionAnsweredTimestamp,
          includeCompletedTimestamp: this.includeCompletedTimestamp,
          includeCash: this.includeCash,
          metricsToInclude: this.metricsToInclude,
          referencesToInclude: this.referencesToInclude,
          gamesToInclude: this.gamesToInclude,
          includeHelpClicks: this.includeHelpClicks,
          timeZone: defaultDateOptions.timeZone,
          locale: defaultDateOptions.locale,
          classesToInclude: this.classesToInclude,
          includeAllUsers: this.includeAllUsers,
          includeDuration: this.includeDuration,
          perChallengeTeamMetrics: this.perChallengeTeamMetrics,
          includePercentIndividualMetrics: this.includePercentIndividualMetrics,
          requireClass: this.requireClass,
        });

        this.flashSuccess({ message: 'Usage data exporting to CSV...' });

        const csvString = objectToCSV(dataObject);

        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
        FileSaver.saveAs(blob, 'UsageData.csv');

        this.$nextTick(() => { this.submitted = false; }); // lets you download again if you want
      } catch (_) {
        const message = 'Error downloading usage data, please try again';
        this.flashDanger({ message });
        this.submitted = false;
      }
    },
    async saveConfig() {
      const defaultDateOptions = Intl.DateTimeFormat().resolvedOptions();
      const savedConfig = await this.createUsageDataConfig({
        name: this.configNameToSave,
        includeEmail: this.includeEmail,
        includeName: this.includeName,
        includeStudentId: this.includeStudentId,
        includeLastSignedInTimestamp: this.includeLastSignedInTimestamp,
        includeLastActiveTimestamp: this.includeLastActiveTimestamp,
        includeActivity: this.includeActivity,
        includeIndividualDecisionProgress: this.includeIndividualDecisionProgress,
        includeTeamDecisionProgress: this.includeTeamDecisionProgress,
        includeLastQuestionAnsweredTimestamp: this.includeLastQuestionAnsweredTimestamp,
        includeCompletedTimestamp: this.includeCompletedTimestamp,
        includeCash: this.includeCash,
        metricsToInclude: this.metricsToInclude,
        referencesToInclude: this.referencesToInclude,
        gamesToInclude: this.gamesToInclude,
        includeHelpClicks: this.includeHelpClicks,
        timeZone: defaultDateOptions.timeZone,
        locale: defaultDateOptions.locale,
        classesToInclude: this.classesToInclude,
        includeAllUsers: this.includeAllUsers,
        includeDuration: this.includeDuration,
        perChallengeTeamMetrics: this.perChallengeTeamMetrics,
        includePercentIndividualMetrics: this.includePercentIndividualMetrics,
        requireClass: this.requireClass,
      });
      const index = this.configs.findIndex(config => config.name === savedConfig.name);
      if (index > -1) {
        this.$set(this.configs, index, savedConfig);
      } else {
        this.configs.push(savedConfig);
      }
    },
    async onConfigSelected() {
      if (this.selectedConfigName) {
        const config = await this.getUsageDataConfig({ configName: this.selectedConfigName });
        this.configNameToSave = config.name;
        this.includeEmail = config.includeEmail;
        this.includeName = config.includeName;
        this.includeStudentId = config.includeStudentId;
        this.includeLastSignedInTimestamp = config.includeLastSignedInTimestamp;
        this.includeLastActiveTimestamp = config.includeLastActiveTimestamp;
        this.includeActivity = config.includeActivity;
        this.includeIndividualDecisionProgress = config.includeIndividualDecisionProgress;
        this.includeTeamDecisionProgress = config.includeTeamDecisionProgress;
        this.includeLastQuestionAnsweredTimestamp = config.includeLastQuestionAnsweredTimestamp;
        this.includeCompletedTimestamp = config.includeCompletedTimestamp;
        this.includeCash = config.includeCash;
        this.referencesToInclude = config.referencesToInclude;
        this.gamesToInclude = config.gamesToInclude;
        this.includeHelpClicks = config.includeHelpClicks;
        this.timeZone = config.timeZone;
        this.locale = config.locale;
        this.classesToInclude = config.classesToInclude;
        this.includeAllUsers = config.includeAllUsers;
        this.includeDuration = config.includeDuration;
        this.perChallengeTeamMetrics = config.perChallengeTeamMetrics;
        this.includePercentIndividualMetrics = config.includePercentIndividualMetrics;
        this.requireClass = config.requireClass;
        await this.updateMetrics();
        this.metricsToInclude = config.metricsToInclude || [];
      }
    },
    async deleteConfig() {
      if (this.selectedConfigName) {
        // eslint-disable-next-line no-alert
        if (confirm('Permanently delete the selected report configuration?')) {
          await this.deleteUsageDataConfig({ configName: this.selectedConfigName });
          this.selectedConfigName = '';
          try {
            this.configs = await this.getUsageDataConfigs();
          } catch (err) {
            const message = 'Error getting report configurations, please refresh';
            this.flashDanger({ message });
          }
        }
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.login-form {
  background-color: white;
  margin-top: 50px;
  padding: 20px;
  position: relative;
}
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/UsageDataRequest.vue